import {
    Overlay,
    OverlayConfig,
    OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, Output, TemplateRef, ViewContainerRef, inject, input, output, viewChild } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';

import { PlanModel } from '~/app/shared/types/api/authorisation/plan-model.type';
import { SidebarItemAuthorisation } from '~/app/shared/types/sidebar-item-authorisation.type';
import { SidebarChildItem, SidebarItem } from '~/app/shared/types/sidebar-item.type';
import { User } from '~/app/shared/types/user/user.type';

import { DropdownPanelAltComponent } from '../dropdown-panel-alt/dropdown-panel-alt.component';
import { DropdownPanelBodyComponent } from '../dropdown-panel-body/dropdown-panel-body.component';
import { IconComponent } from '~/app/shared/components/elements/icon/icon.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { SidebarMenuItemComponent } from '../sidebar-menu-item/sidebar-menu-item.component';
import { TrackingDirective } from '~/app/shared/directives/matomo/tracking.directive';
import {AuthorisationDirective} from "~/app/domains/authorisation/directives/authorisation.directive";

@UntilDestroy()
@Component({
    selector: 'eb-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        IconComponent,
        SidebarMenuItemComponent,
        LabelComponent,
        AuthorisationDirective,
        DropdownPanelAltComponent,
        DropdownPanelBodyComponent,
        TranslocoPipe,
        TrackingDirective,
    ]
})
export class SidebarComponent {
    overlay = inject(Overlay);
    viewContainerRef = inject(ViewContainerRef);

    public readonly user = input<User | null>(null);

    public readonly items = input<SidebarItemAuthorisation[]>([]);

    @Input()
    public plan: PlanModel | null = null;

    public readonly hasFreeTrial = input(false);

    @Input()
    public userItem!: SidebarItem;

    public readonly mainItems = input<SidebarItemAuthorisation[]>([]);

    public readonly otherItems = input<SidebarItemAuthorisation[]>([]);

    public readonly hasRegistration = input(true);

    public readonly version = input<string | null>(null);

    public versionClick = output();

    @HostBinding('class.expanded')
    @Input()
    public expanded = true;

    readonly subMenuTemplate = viewChild.required<TemplateRef<HTMLElement>>('subMenuTemplate');

    @Output()
    public signup = new EventEmitter();

    @Output()
    public freetrial = new EventEmitter();

    @Output()
    public toggleMenu = new EventEmitter();

    public overlayRef?: OverlayRef;

    public currentSubMenuItems: SidebarItem[] = [];

    @Input()
    public expandedSubmenuKey: string | null = null;

    @Input()
    public activeChildKey: string | null = null;

    public trackByItem(index: number, item: SidebarItem) {
        return item.key;
    }

    public clickItem(item: SidebarItem, menuItem: SidebarMenuItemComponent): void {
        if (item.children && item.children.length > 0) {
            if (!this.expanded) {
                this.toggleMenu.emit();
            }
            this.expandedSubmenuKey = this.expandedSubmenuKey === item.key ? null : item.key;
            return;
        }

        // Close any open submenu when clicking a different main item
        this.expandedSubmenuKey = null;
        this.activeChildKey = null;

        if (item.action) {
            return item.action();
        }
        if (item.items && item.items.length > 0) {
            this.currentSubMenuItems = item.items;
            this.openOverlay(menuItem.elementRef);
        }

        return undefined;
    }

    public onChildClick(child: SidebarChildItem): void {
        if (!child.locked) {
            this.activeChildKey = child.key;
        }
        if (child.action) {
            child.action();
        }
    }

    public clickSubItem(item: SidebarItem) {
        if (item.action) {
            item.action();
        }
        this.overlayRef?.detach();
    }

    public planClick(plan: PlanModel) {
        if (plan.isFreemium) {
            this.freetrial.emit();
        }
    }

    private openOverlay(button: ElementRef<HTMLElement>) {
        const config = new OverlayConfig({
            hasBackdrop: true,
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(button)
                .setOrigin(button)
                .withPositions([
                    {
                        offsetX: 0,
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        offsetX: 0,
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                ])
                .withFlexibleDimensions(false)
                .withPush(false),
        });

        this.overlayRef = this.overlay.create(config);

        const portal = new TemplatePortal(this.subMenuTemplate(), this.viewContainerRef);

        this.overlayRef.attach(portal);
        this.overlayRef.backdropClick()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.overlayRef?.detach();
            });
    }
}
